import type { MaterialResource } from '@/material/web/api'
import MainProject from '@/model/MainProject'
import {
  applySkinPackageToProject,
  applySkinResources,
  parseSkinOuiPackage,
  trimSkinPathName,
} from '@/features/skin-loader/runtime'
import { getMaterialSourceDownloadUrl } from '@/features/material-library/apply'
import {
  buildProjectResourceObjectName,
  type MaterialUploadProgressStage,
  uploadMaterialFiles,
} from '@/features/material-library/upload'
import { useAuthStore } from '@/features/auth'
import { useWorkspaceStore } from '@/store/workspace'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { validateImportedProjectResourcePaths } from '@/features/project-mall/runtime'

type UiApplyProgressStage = MaterialUploadProgressStage | 'parsing' | 'downloading'

export type UiMaterialApplyResult = {
  fileCount: number
  folderSegments: string[]
  ouiFileName: string
}

export type UiMaterialApplyProgress = (
  stage: UiApplyProgressStage,
  percent: number,
  detail?: string,
) => void

function toOverallPercent(start: number, end: number, percent: number): number {
  const clamped = Math.max(0, Math.min(100, percent))
  return Math.round(start + ((end - start) * clamped) / 100)
}

function isOuiResource(resource: MaterialResource): boolean {
  return trimSkinPathName(resource.extra).toLowerCase().endsWith('.oui')
}

function mergeUint8Arrays(chunks: Uint8Array[], totalLength: number): Uint8Array {
  const merged = new Uint8Array(totalLength)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }
  return merged
}

async function downloadResourceBlob(
  resource: MaterialResource,
  onProgress?: (percent: number, detail: string) => void,
): Promise<Blob> {
  const response = await fetch(getMaterialSourceDownloadUrl(resource))
  if (!response.ok) {
    throw new Error(`素材下载失败：HTTP ${response.status}`)
  }
  const contentLength = Number(response.headers.get('content-length') || '0')
  if (!response.body) {
    onProgress?.(100, '下载皮肤包…')
    return await response.blob()
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let receivedLength = 0
  let unknownLengthPercent = 0

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    if (!value?.length) continue
    chunks.push(value)
    receivedLength += value.length
    if (contentLength > 0) {
      onProgress?.(Math.min(100, Math.round((receivedLength / contentLength) * 100)), '下载皮肤包…')
    } else {
      unknownLengthPercent = Math.min(95, unknownLengthPercent + 5)
      onProgress?.(unknownLengthPercent, '下载皮肤包…')
    }
  }

  onProgress?.(100, '下载皮肤包完成')
  const buffer = mergeUint8Arrays(chunks, receivedLength).buffer.slice(0) as ArrayBuffer
  return new Blob([buffer], {
    type: response.headers.get('content-type') || 'application/octet-stream',
  })
}

async function loadMaterialSkinPackage(params: {
  resources: MaterialResource[]
  dataVersion: number
  onProgress?: UiMaterialApplyProgress
}): Promise<{
  skinPackage: ReturnType<typeof parseSkinOuiPackage>
  ouiFileName: string
}> {
  const ouiResource = params.resources.find((item) => isOuiResource(item))
  if (!ouiResource?.resmd5) {
    throw new Error('当前 UI 素材缺少 .oui 文件，无法应用')
  }

  params.onProgress?.('downloading', toOverallPercent(0, 25, 0), '下载皮肤包…')
  const ouiBlob = await downloadResourceBlob(ouiResource, (percent, detail) => {
    params.onProgress?.('downloading', toOverallPercent(0, 25, percent), detail)
  })
  params.onProgress?.('parsing', toOverallPercent(25, 45, 0), '解析皮肤资源…')
  const skinPackage = parseSkinOuiPackage({
    project: new MainProject(),
    source: await BrowserFileUtils.blobToArrayBuffer(ouiBlob),
    dataVersion: params.dataVersion,
    onProgress: (percent, status) => {
      params.onProgress?.('parsing', toOverallPercent(25, 45, percent), status)
    },
  })
  params.onProgress?.('parsing', toOverallPercent(25, 45, 100), `共解析到 ${skinPackage.entries.length} 个皮肤资源`)
  return {
    skinPackage,
    ouiFileName: trimSkinPathName(ouiResource.extra),
  }
}

export async function applyUiMaterialToWorkspace(params: {
  resources: MaterialResource[]
  onProgress?: UiMaterialApplyProgress
}): Promise<UiMaterialApplyResult> {
  const auth = useAuthStore()
  const workspace = useWorkspaceStore()
  const project = workspace.project as MainProject | null
  if (!auth.loggedIn || !auth.sessionId || !auth.uid) {
    throw new Error('请先登录后再应用素材')
  }
  if (!project) {
    throw new Error('当前没有已打开工程')
  }
  const guid = project.projectHeader.ID?.trim()
  if (!guid) {
    throw new Error('当前工程缺少 guid，暂时无法应用素材到云端资源库')
  }

  const { skinPackage, ouiFileName } = await loadMaterialSkinPackage({
    resources: params.resources,
    dataVersion: project.projectConstant.dataVersion || project.dataVer,
    onProgress: params.onProgress,
  })

  const version = String(project.projectHeader.Version || project.projectConstant.gameVersion || 1)
  const uploadedResults = await uploadMaterialFiles({
    guid,
    sessionId: auth.sessionId,
    version,
    items: skinPackage.entries.map((entry) => ({
      file: entry.file,
      objectName: buildProjectResourceObjectName({
        guid,
        category: entry.category,
        folderSegments: entry.folderSegments,
        fileName: entry.fileName,
      }),
      resultFileName: entry.fileName,
    })),
    onProgress: (stage, percent) => {
      const detail =
        stage === 'hashing'
          ? '计算皮肤资源校验…'
          : stage === 'uploading'
            ? '上传皮肤资源…'
            : '等待皮肤资源入库…'
      params.onProgress?.(stage, toOverallPercent(45, 90, percent), detail)
    },
  })
  applySkinPackageToProject(project, skinPackage)
  params.onProgress?.('parsing', toOverallPercent(90, 100, 10), '写入皮肤资源…')

  const applied = await applySkinResources({
    project,
    roots: workspace.virtualResourceRoots,
    entries: skinPackage.entries,
    includeReferencedPlaceholders: false,
    onProgress: (percent, status) => {
      params.onProgress?.('parsing', toOverallPercent(90, 100, percent), status || '写入皮肤资源…')
    },
    resolveResource: async (_entry, index) => {
      const uploaded = uploadedResults[index]
      if (!uploaded) {
        throw new Error(`皮肤资源上传结果缺失：#${index + 1}`)
      }
      return {
        resourceUrl: uploaded.url,
        fileName: uploaded.fileName,
      }
    },
  })
  validateImportedProjectResourcePaths(project, applied.roots.graphic)

  workspace.replaceVirtualResourceRoots(applied.roots)
  await workspace.uploadVirtualResourcesToCloud()
  workspace.revealResourceBrowserFolder('graphic', ['UI'])

  return {
    fileCount: skinPackage.entries.length,
    folderSegments: ['UI'],
    ouiFileName,
  }
}
